import { Collapse } from 'omakase';
import {
  FormControl as MuiFormControl,
  FormGroup as MuiFormGroup,
  FormHelperText as MuiFormHelperText,
  FormLabel as MuiFormLabel,
  RadioGroup as MuiRadioGroup,
} from '@material-ui/core';
import { RadioCoreType } from 'omakase/SelectionControls/SelectionControlsCore';
import { ResponsiveParameter } from 'omakase/utils/responsiveSizeMap';
import { Theme } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import {
  baseElement,
  closeOrShowLink,
  formGroup,
  header,
  helperText,
  invisibleChoices,
  labelText,
  staticGrid,
} from 'omakase/SelectionControls/selectionControlsGroupCssDefs';
import Divider from 'omakase/Divider/Divider';
import PlainButton from 'omakase/PlainButton/PlainButton';
import React from 'react';

interface SelectionControlsGroupTypeProps {
  type: RadioCoreType;
}

export type SelectionControlsGroupSizes = 'small' | 'large' | 'jumbo';
export type SelectionControlsGroupSize = ResponsiveParameter<SelectionControlsGroupSizes>;

export type SelectionControlsGroupWeight = 'book' | 'medium' | 'bold';

interface SelectionControlsGroupCoreProps {
  /**
   * Label text for the group
   */
  label: string;
  /**
   * Whether the label is visually hidden
   */
  hideLabel?: boolean;
  /**
   * Size of the group's label
   */
  labelSize?: SelectionControlsGroupSize;
  /**
   * Weight of the group's label
   */
  labelWeight?: SelectionControlsGroupWeight;
  /**
   * Helper text for the group's label
   */
  helperText?: string;
  /**
   * Whether or not the group's field is in an error state
   */
  error?: boolean;
  /**
   * Error text to display if the group's field is in an error state
   */
  errorText?: string;
  /**
   * The aria-label to use will default to 'label'; use this only if aria-label should be different.
   */
  'aria-label'?: string;
  /**
   * Whether or not to hide the group's top divider
   */
  includeTopDivider?: boolean;
  /**
   * Whether or not to hide the group's bottom divider
   */
  includeBottomDivider?: boolean;
  /**
   * Whether or not the group's field / controls are in a disabled state
   */
  disabled?: boolean;
  /**
   * The onChange event for the group's field
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  /**
   * The field name for the group
   */
  name?: string;
  /**
   * The group's value
   */
  value?: string;
  className?: string;
}

interface ExpandCollapseProps {
  /**
   * Display mode for the group
   */
  disclosureMode: 'expandcollapse';
  /**
   * Whether the radio group should be open on-mount, or closed. Thereafter this is handled
   * in internal component state; it is not a "controlled component"-style prop.
   */
  initiallyClosed: boolean;
  /**
   * If disclosureMode is 'expandcollapse', this represents the number of options to display when the group is collapsed
   */
  collapseAfterCount?: number;
  smColumns?: undefined;
}

interface AccordionProps {
  disclosureMode: 'accordion';
  initiallyClosed: boolean;
  collapseAfterCount?: undefined;
  smColumns?: undefined;
}

interface StaticProps {
  disclosureMode: 'static';
  /**
   * This is not meaningful in disclosureMode = static; as an implementation convenience we give it
   * this type.
   */
  initiallyClosed?: undefined;
  collapseAfterCount?: undefined;
  smColumns?: number;
}

export type DisclosureProps = ExpandCollapseProps | AccordionProps | StaticProps;

export type SelectionControlsGroupProps = SelectionControlsGroupCoreProps & DisclosureProps;

const DEFAULT_COLLAPSE_AFTER_COUNT = 8;

/**
 * Shared implementation of grouping behaviors for CheckboxGroup and RadioButtonGroups.
 * Should NOT be used in production.
 */
const SelectionControlsGroupCore: React.FunctionComponent<
  SelectionControlsGroupProps & SelectionControlsGroupTypeProps
> = ({
  'aria-label': ariaLabel,
  collapseAfterCount = DEFAULT_COLLAPSE_AFTER_COUNT,
  label,
  hideLabel = false,
  labelSize = 'large',
  labelWeight = 'medium',
  helperText: helperTextProp,
  disclosureMode,
  smColumns = 1,
  disabled,
  initiallyClosed = false,
  children,
  error,
  includeTopDivider,
  includeBottomDivider,
  type,
  errorText,
  name,
  onChange,
  value,
  ...rest
}) => {
  const effectiveAriaLabel = ariaLabel ?? label;

  const [closed, setClosed] = React.useState(initiallyClosed);

  const hasSecondaryLabelText = !!((error && errorText) || helperTextProp);

  const labeling = [
    <span key="first" css={(theme: Theme) => labelText(theme, labelSize, labelWeight)}>
      {label}
    </span>,
    hasSecondaryLabelText ? (
      <MuiFormHelperText key="second" css={(theme: Theme) => helperText(theme, labelSize)}>
        {error ? errorText : helperTextProp}
      </MuiFormHelperText>
    ) : null,
  ];

  const makeLabeling = (options?: { plain: boolean }) => (
    <MuiFormLabel
      component="legend"
      css={(theme: Theme) => header(theme, { hideLabel, plain: options?.plain })}
    >
      {labeling}
    </MuiFormLabel>
  );

  const showAllText = `Show all “${label}”`;
  const closeText = `Close “${label}”`;

  const getContent = () => {
    switch (disclosureMode) {
      case 'accordion': {
        const childrenWithLabelingHead = [makeLabeling(), React.Children.toArray(children)];
        return (
          <Collapse label={label} isFullBleed startsExpanded={!initiallyClosed}>
            {childrenWithLabelingHead}
          </Collapse>
        );
      }
      case 'expandcollapse':
        if (React.Children.count(children) > collapseAfterCount) {
          if (closed) {
            const visibleChildren: React.ReactElement[] = [];
            const invisibleChildren: React.ReactElement[] = [];
            React.Children.forEach(children, (child, index) => {
              (index < collapseAfterCount ? visibleChildren : invisibleChildren).push(
                child as React.ReactElement
              );
            });
            return (
              <>
                {makeLabeling({ plain: true })}
                {visibleChildren}
                <PlainButton
                  css={closeOrShowLink}
                  onClick={() => setClosed(false)}
                  variant="primary"
                  size="medium"
                  type="button"
                >
                  {showAllText}
                </PlainButton>
                <div css={invisibleChoices}>{invisibleChildren}</div>
              </>
            );
          } else {
            return (
              <>
                {makeLabeling({ plain: true })}
                {children}
                <PlainButton
                  css={closeOrShowLink}
                  onClick={() => setClosed(true)}
                  variant="primary"
                  size="medium"
                  type="button"
                >
                  {closeText}
                </PlainButton>
              </>
            );
          }
        } else {
          return (
            <>
              {makeLabeling({ plain: true })}
              {children}
            </>
          );
        }
        break;
      case 'static':
        return (
          <>
            {makeLabeling({ plain: true })}
            <div css={(theme: Theme) => staticGrid(theme, smColumns)}>{children}</div>
          </>
        );
      default:
        assertNever(disclosureMode);
    }
  };

  const content = getContent();

  const groupComponent =
    type === 'checkbox' ? (
      <MuiFormGroup css={formGroup}>{content}</MuiFormGroup>
    ) : (
      <MuiRadioGroup css={formGroup} name={name} onChange={onChange} value={value}>
        {content}
      </MuiRadioGroup>
    );

  return (
    <MuiFormControl
      component="fieldset"
      aria-label={effectiveAriaLabel}
      error={error}
      disabled={disabled}
      css={baseElement}
      {...rest}
    >
      {includeTopDivider && <Divider position="middle" />}
      {groupComponent}
      {includeBottomDivider && <Divider position="middle" />}
    </MuiFormControl>
  );
};

export default SelectionControlsGroupCore;
